import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { getApiBaseUrl } from "./env";
import { refreshSession } from "./authSession";
import {
  clearAuthState,
  getAccessToken,
  getActiveOrgId,
  getWorkspaceGeneration,
  setAuthNotice,
  setPostLoginRedirectPath,
  useRefreshCookie,
} from "./authStorage";
import { isProtectedRoute, parseRoute } from "./router";

type AuthRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
  _workspaceInitialized?: boolean;
  _workspace?: { orgId: string | null; generation: number };
};

function isGlobalEndpoint(url = "") {
  const path = new URL(url, "https://api.invalid/").pathname.replace(/^\/api(?=\/)/, "");
  if (["/orgs", "/orgs/current", "/orgs/active"].includes(path)) return true;
  return ["/auth", "/user-preferences"].some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function assertCurrentWorkspace(config?: AuthRequestConfig) {
  const scope = config?._workspace;
  if (scope && (scope.generation !== getWorkspaceGeneration() || scope.orgId !== getActiveOrgId())) {
    throw new axios.CanceledError("Workspace changed. Request cancelled.");
  }
}

function apiErrorMessage(error: AxiosError): string | undefined {
  const data = error.response?.data as
    | { error?: string; message?: string }
    | string
    | undefined;
  if (typeof data === "string") {
    return data.trim() || undefined;
  }
  return data?.error ?? data?.message;
}

let apiClient: AxiosInstance | null = null;

export function isSessionExpiredResponse(
  response?: AxiosResponse
) {
  if (!response) {
    return false;
  }
  if (response.status === 401) {
    return true;
  }
  const data = response.data as
    | { error?: string; message?: string }
    | string
    | undefined;
  const message =
    typeof data === "string" ? data : data?.error ?? data?.message;
  if (!message) {
    return false;
  }
  return message.toLowerCase().includes("access token has expired");
}

function handleSessionExpiry() {
  const currentPath = window.location.pathname || "/";
  const routeInfo = parseRoute(currentPath);
  if (isProtectedRoute(routeInfo.name)) {
    setPostLoginRedirectPath(currentPath, window.location.search);
  }
  setAuthNotice("Your session expired. Please sign in again.");
  clearAuthState();
  redirectToSignIn();
}

function redirectToSignIn() {
  if (window.location.pathname === "/") {
    return;
  }
  window.history.pushState({}, "", "/");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function getApiClient() {
  if (apiClient) {
    return apiClient;
  }

  apiClient = axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: useRefreshCookie(),
  });

  apiClient.interceptors.request.use((request) => {
    const config = request as AuthRequestConfig;
    if (!config._workspaceInitialized) {
      config._workspaceInitialized = true;
      const orgId = getActiveOrgId();
      if (!isGlobalEndpoint(config.url)) config._workspace = { orgId, generation: getWorkspaceGeneration() };
      if (orgId) config.headers.set("x-org-id", orgId);
    }
    assertCurrentWorkspace(config);
    const token = getAccessToken();
    if (token) config.headers.set("Authorization", `Bearer ${token}`);
    return config;
  }, (error: unknown) => { throw error; }, { synchronous: true });

  apiClient.interceptors.response.use(
    (response) => {
      assertCurrentWorkspace(response.config as AuthRequestConfig);
      return response;
    },
    async (error: AxiosError) => {
      const response = error.response;
      const config = error.config as AuthRequestConfig | undefined;
      assertCurrentWorkspace(config);
      if (!response || !config) {
        return Promise.reject(error);
      }
      if (config.skipAuthRefresh) {
        return Promise.reject(new Error(apiErrorMessage(error) ?? error.message));
      }
      const isExpiredResponse = isSessionExpiredResponse(response);
      if (config._retry) {
        if (isExpiredResponse) {
          handleSessionExpiry();
        }
        return Promise.reject(error);
      }
      if (!isExpiredResponse && response.status !== 401) {
        return Promise.reject(new Error(apiErrorMessage(error) ?? error.message));
      }

      config._retry = true;
      try {
        assertCurrentWorkspace(config);
        await refreshSession();
        assertCurrentWorkspace(config);
        const token = getAccessToken();
        if (token) {
          config.headers.set("Authorization", `Bearer ${token}`);
        }
        return apiClient?.request(config);
      } catch (refreshError) {
        assertCurrentWorkspace(config);
        handleSessionExpiry();
        return Promise.reject(refreshError);
      }
    }
  );

  return apiClient;
}
